class LoadMoreButton extends HTMLElement {
    constructor() {
      super();
      this.btn = this.querySelector('a[role="button"]');
      if (!this.btn) return;

      // Cache for preloaded pages
      this.preloadedPages = new Map();
      this.preloadInProgress = new Set();

      // Set up click event listener
      this.btn.addEventListener('click', (event) => {
        event.preventDefault(); // Prevent default link navigation
        event.stopPropagation(); // Stop event bubbling
        this.loadMore(event);
      });

      // Additional safety: prevent any form of navigation
      this.btn.addEventListener('mousedown', (event) => {
        if (event.button === 1) { // Middle mouse button
          event.preventDefault();
        }
      });

      // Set up intersection observer for infinity scroll
      // But only if we don't have a cached state that says we are done or restoring
      this.#setupInfinityScroll();
      
      // Check if we are restoring from cache to avoid double-fetching
      const isRestoring = !!window.sessionStorage.getItem('collectionGridCache:' + window.location.pathname + window.location.search);

      // Start preloading the next few pages, but only if we are NOT restoring from cache
      // (Restoring is handled by product-grid-caching.js)
      if (!isRestoring) {
        this.#startPreloading();
      }
    }

    /**
     * Sets up the Intersection Observer for automatic loading when the button comes into view
     * Uses rootMargin to trigger loading slightly before the button enters the viewport
     */
    #setupInfinityScroll() {
      const options = {
        root: null, // Use the viewport as the root
        rootMargin: '2000px 0px', // Trigger 200px before the button enters viewport
        threshold: 0.1 // Trigger when 10% of the element is visible
      };

      this.observer = new IntersectionObserver(this.#handleIntersection.bind(this), options);
      this.observer.observe(this);
    }

    /**
     * Handles intersection events - triggers loading when button comes into view
     */
    #handleIntersection(entries) {
      entries.forEach(entry => {
        if (entry.isIntersecting && this.btn.getAttribute('aria-disabled') !== 'true') {
          this.loadMore();
        }
      });
    }
    updateURL(nextUrl) {
      if (!nextUrl) return;
      // Clean the phcursor parameter from the URL for better UX
      const cleanUrl = this.#cleanPhcursorFromURL(nextUrl);
      window.history.replaceState({ }, '', cleanUrl);
      
      // Update canonical URL
      this.#updateCanonicalURL(cleanUrl);
      
      // Notify other components that load-more has updated the URL
      // This allows scroll-to-product-grid to sync its page tracking
      const event = new CustomEvent('loadmore:url-updated', {
        detail: { 
          url: cleanUrl,
          source: 'load-more'
        }
      });
      document.dispatchEvent(event);
    }

    /**
     * Updates the canonical URL in the document head
     * @param {string} url - The new canonical URL
     */
    #updateCanonicalURL(url) {
      const canonicalLink = document.querySelector('link[rel="canonical"]');
      if (canonicalLink) {
        // Create full URL from relative path
        const fullUrl = new URL(url, window.location.origin).href;
        canonicalLink.href = fullUrl;
      }
    }

    /**
     * Removes the phcursor parameter from a URL while preserving all other parameters
     * Also handles page=1 cleanup by removing page parameter when it equals 1
     * @param {string} url - The URL to clean
     * @returns {string} The cleaned URL without phcursor parameter and proper page=1 handling
     */
    #cleanPhcursorFromURL(url) {
      try {
        const urlObj = new URL(url, window.location.origin);
        urlObj.searchParams.delete('phcursor');
        
        // Remove content block pagination parameter
        const cbParam = this.#getContentBlockParam();
        if (cbParam) {
          urlObj.searchParams.delete(cbParam);
        }
        
        // Remove page parameter if it's 1
        const pageParam = urlObj.searchParams.get('page');
        if (pageParam === '1') {
          urlObj.searchParams.delete('page');
        }
        
        // Return the cleaned URL
        const cleanPath = urlObj.pathname + (urlObj.search || '');
        return cleanPath === '/' ? '/' : cleanPath;
      } catch (error) {
        // Fallback: if URL parsing fails, try regex cleanup
        return url.replace(/[?&]phcursor=[^&]*/g, '').replace(/[?&]page=1(?=&|$)/g, '').replace(/^\?&/, '?');
      }
    }

      loadMore(event = null) {
        if (event) {
          event.preventDefault();
        }

        const btn = this.btn;
        btn.setAttribute('aria-disabled', 'true');
        
        // Add loading state to button
        const buttonText = btn.querySelector('.load-more-button__text');
        const spinner = btn.querySelector('.load-more-button__spinner');
        if (buttonText) buttonText.style.opacity = '0.7';
        if (spinner) spinner.style.display = 'inline-block';
        
        let nextUrl = btn.querySelector('[data-next-url]').value;

        // Add content block pagination parameter
        const cbParam = this.#getContentBlockParam();
        if (cbParam) {
          try {
            const urlObj = new URL(nextUrl, window.location.origin);
            const page = urlObj.searchParams.get('page');
            if (page) {
              urlObj.searchParams.set(cbParam, page);
              nextUrl = urlObj.pathname + urlObj.search;
            }
          } catch (e) {
            console.error('Error updating content block param', e);
          }
        }
        
        // Check if we have this page preloaded for instant replacement
        if (this.preloadedPages.has(nextUrl)) {
          const preloadedData = this.preloadedPages.get(nextUrl);
          this.#handleLoadedContent(preloadedData.items, preloadedData.newBtn, true);
          this.preloadedPages.delete(nextUrl); // Remove used cache
          this.updateURL(nextUrl);
          
          // Continue preloading the next pages after using cached content
          this.#continuePreloadingAfterLoad();
          return;
        }
        
        // Not preloaded - show skeletons and fetch
        this.#renderSkeletonPlaceholders();
        this.updateURL(nextUrl);

        fetch(nextUrl)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.text();
        })
        .then((responseText) => {
          const parsedData = this.#parseResponseData(responseText);
          this.#handleLoadedContent(parsedData.items, parsedData.newBtn, false);
          
          // Continue preloading the next pages after fresh load
          this.#continuePreloadingAfterLoad();
        })
        .catch((error) => {
          this.#handleLoadError(error);
        });
    }

    /**
     * Renders skeleton placeholders immediately when loading is triggered
     * Provides instant visual feedback before real content loads
     */
    #renderSkeletonPlaceholders() {
      const destination = document.getElementById('ProductGridContainer').querySelector('.product-grid');
      if (!destination) return;

      // Get the expected number of products per page from the load more button data
      // We'll extract this from the current page structure or use a reasonable default
      const expectedProducts = this.#getExpectedProductCount();
      
      // Create skeleton placeholders
      for (let i = 0; i < expectedProducts; i++) {
        const skeletonElement = this.#createSkeletonCard(i);
        destination.appendChild(skeletonElement);
      }
    }

    /**
     * Creates a single skeleton placeholder card
     * @param {number} index - The index of the skeleton for unique identification
     * @returns {HTMLElement} The skeleton card element
     */
    #createSkeletonCard(index) {
      const li = document.createElement('li');
      li.className = 'grid__item grid__item--skeleton-loadmore grid__item--loader';
      li.setAttribute('data-skeleton-index', index);
      li.setAttribute('aria-label', 'Loading product...');
      
      // Use the same skeleton structure as your existing sc-product-card-loader with enhanced animations
      li.innerHTML = `
        <div class="card-wrapper skeleton-loadmore-wrapper" aria-hidden="true">
          <div class="card-information">
            <div class="card-information__wrapper">
              <h3 class="card-information__text h5">
                <span class="full-unstyled-link skeleton-shimmer skeleton-pulse">&nbsp;</span>
              </h3>
              <div class="price skeleton-shimmer skeleton-pulse">
                <div><div class="price__regular">
                    <span class="price-item price-item--regular">
                      &nbsp;
                    </span>
                  </div>
                  <div class="price__sale">
                      <span>
                        <s class="price-item price-item--regular"></s>
                      </span>
                    <span class="price-item price-item--sale price-item--last">
                      &nbsp;
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="card card--product">
            <div class="card__inner">
              <div>
                <div class="media media--transparent media--square media--hover-effect">
                  <span class="motion-reduce product-card-temp__image skeleton-shimmer skeleton-pulse"></span>
                </div>
              </div>
            </div>
          </div>
        </div>
      `;
      
      return li;
    }

    /**
     * Replaces skeleton placeholders with real content as it loads
     * @param {NodeList} realItems - The real product items from the API response
     * @param {HTMLElement} destination - The grid container
     */
    #replaceSkeletonsWithContent(realItems, destination) {
      const skeletons = destination.querySelectorAll('.grid__item--skeleton-loadmore');
      
      // Store multiple scroll reference points for better stability
      const initialScrollY = window.scrollY;
      const viewportHeight = window.innerHeight;
      const scrolledFromTop = initialScrollY;
      
      // Find a stable reference element above the viewport for anchor-based scrolling
      const referenceElement = this.#findStableScrollReference();
      const referenceOffset = referenceElement ? referenceElement.offsetTop : 0;
      
      // Batch DOM operations to minimize reflows
      const fragment = document.createDocumentFragment();
      const skeletonParent = skeletons[0]?.parentNode;
      
      // Replace skeletons with real content in a batch
      for (let i = 0; i < realItems.length && i < skeletons.length; i++) {
        const skeleton = skeletons[i];
        const realItem = realItems[i];
        
        // Set up the real item before insertion to avoid layout shifts
        realItem.style.opacity = '0';
        realItem.style.transition = 'opacity 0.2s ease';
        
        // Replace immediately
        skeleton.parentNode.replaceChild(realItem, skeleton);
        
        // Fade in smoothly
        setTimeout(() => {
          realItem.style.opacity = '1';
        }, i * 15);
      }
      
      // Remove any remaining skeletons
      const remainingSkeletons = destination.querySelectorAll('.grid__item--skeleton-loadmore');
      remainingSkeletons.forEach(skeleton => skeleton.remove());
      
      // Advanced scroll position maintenance
      requestAnimationFrame(() => {
        // Use reference element if available for more stable positioning
        if (referenceElement) {
          const newReferenceOffset = referenceElement.offsetTop;
          const offsetDifference = newReferenceOffset - referenceOffset;
          
          if (Math.abs(offsetDifference) > 2) {
            window.scrollTo(0, initialScrollY + offsetDifference);
          }
        } else {
          // Fallback: maintain relative scroll position
          const currentScrollableHeight = document.documentElement.scrollHeight - viewportHeight;
          const targetScrollY = Math.min(initialScrollY, currentScrollableHeight);
          
          if (Math.abs(window.scrollY - targetScrollY) > 5) {
            window.scrollTo(0, targetScrollY);
          }
        }
      });
    }

    /**
     * Finds a stable element above the viewport to use as scroll reference
     * @returns {HTMLElement|null} A stable reference element
     */
    #findStableScrollReference() {
      const currentScrollY = window.scrollY;
      const products = document.querySelectorAll('#product-grid .grid__item:not(.grid__item--skeleton-loadmore)');
      
      // Find the last product that's above the current viewport
      for (let i = products.length - 1; i >= 0; i--) {
        const product = products[i];
        const productTop = product.offsetTop;
        
        if (productTop < currentScrollY - 100) { // 100px buffer above viewport
          return product;
        }
      }
      
      return null;
    }

    /**
     * Determines the expected number of products per page
     * @returns {number} Expected number of products to load
     */
    #getExpectedProductCount() {
      // Calculate remaining products based on pagination data
      const totalPagesInput = this.btn.querySelector('[data-total-pages]');
      const currentPageInput = this.btn.querySelector('[data-current-page]');
      const pageSizeInput = this.btn.querySelector('[data-page-size]');
      
      if (totalPagesInput && currentPageInput && pageSizeInput) {
        const totalPages = parseInt(totalPagesInput.value, 10);
        const currentPage = parseInt(currentPageInput.value, 10);
        const pageSize = parseInt(pageSizeInput.value, 10);
        const nextPage = currentPage + 1;
        
        // If we're loading the last page, calculate remaining products
        if (nextPage === totalPages) {
          // Get total items from progress bar if available
          const progressBar = this.querySelector('.load-more-button__progress');
          if (progressBar && progressBar.getAttribute('max')) {
            const totalItems = parseInt(progressBar.getAttribute('max'), 10);
            const currentlyLoaded = currentPage * pageSize;
            const remainingItems = totalItems - currentlyLoaded;
            
            return Math.max(1, remainingItems); // At least 1 product
          }
        }
        
        // For other pages, use full page size
        return pageSize;
      }
      
      // Fallback: Try to get exact page size from button data
      if (pageSizeInput && pageSizeInput.value) {
        return parseInt(pageSizeInput.value, 10);
      }
      
      // Fallback: count existing products in current page and assume same for next page
      const existingProducts = document.querySelectorAll('#product-grid .grid__item:not(.grid__item--loader):not(.card-description):not(.grid__item--skeleton-loadmore)');
      if (existingProducts.length > 0) {
        return Math.min(existingProducts.length, 24); // Cap at reasonable maximum
      }
      
      // Final fallback - use the default from schema
      return 24; // Conservative estimate based on your default setting
    }

    /**
     * Starts preloading the next 2-3 pages in the background
     */
    #startPreloading() {
      // Get pagination info
      const totalPagesInput = this.btn.querySelector('[data-total-pages]');
      const currentPageInput = this.btn.querySelector('[data-current-page]');
      
      if (!totalPagesInput || !currentPageInput) return;
      
      const totalPages = parseInt(totalPagesInput.value, 10);
      const currentPage = parseInt(currentPageInput.value, 10);
      
      // Preload next 2-3 pages (depending on how many are left)
      const pagesToPreload = Math.min(3, totalPages - currentPage);
      
      for (let i = 1; i <= pagesToPreload; i++) {
        const targetPage = currentPage + i;
        if (targetPage <= totalPages) {
          // Small delay between preloads to avoid overwhelming the server
          setTimeout(() => {
            this.#preloadPage(targetPage);
          }, i * 200);
        }
      }
    }

    /**
     * Preloads a specific page in the background
     * @param {number} pageNumber - The page number to preload
     */
    #preloadPage(pageNumber) {
      const baseUrl = window.location.pathname + window.location.search;
      let pageUrl;
      
      if (baseUrl.includes('page=')) {
        pageUrl = baseUrl.replace(/page=[0-9]+/, `page=${pageNumber}`);
      } else {
        const separator = baseUrl.includes('?') ? '&' : '?';
        pageUrl = baseUrl + separator + `page=${pageNumber}`;
      }
      
      // Add content block pagination parameter
      const cbParam = this.#getContentBlockParam();
      if (cbParam) {
        try {
          const urlObj = new URL(pageUrl, window.location.origin);
          urlObj.searchParams.set(cbParam, pageNumber);
          pageUrl = urlObj.pathname + urlObj.search;
        } catch (e) {
          // Ignore
        }
      }
      
      // Don't preload if already in progress or cached
      if (this.preloadInProgress.has(pageUrl) || this.preloadedPages.has(pageUrl)) {
        return;
      }
      
      this.preloadInProgress.add(pageUrl);
      
      fetch(pageUrl)
        .then(response => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.text();
        })
        .then(responseText => {
          const parsedData = this.#parseResponseData(responseText);
          
          // Cache the preloaded data
          this.preloadedPages.set(pageUrl, {
            items: parsedData.items,
            newBtn: parsedData.newBtn,
            timestamp: Date.now()
          });
          
          // Continue preloading next pages if this was successful
          this.#continuePreloading(pageNumber);
        })
        .catch(error => {
          // Silently handle preload failures
        })
        .finally(() => {
          this.preloadInProgress.delete(pageUrl);
        });
    }

    /**
     * Continues preloading pattern after a successful preload
     * @param {number} lastPreloadedPage - The page that was just preloaded
     */
    #continuePreloading(lastPreloadedPage) {
      const totalPagesInput = this.btn.querySelector('[data-total-pages]');
      if (!totalPagesInput) return;
      
      const totalPages = parseInt(totalPagesInput.value, 10);
      const nextPageToPreload = lastPreloadedPage + 1;
      
      // Only preload if we haven't reached the limit and there are more pages
      if (nextPageToPreload <= totalPages && this.preloadedPages.size < 3) {
        setTimeout(() => {
          this.#preloadPage(nextPageToPreload);
        }, 300);
      }
    }

    /**
     * Smart continuation of preloading after a page load
     * Only preloads what's actually needed
     */
    #continuePreloadingAfterLoad() {
      const totalPagesInput = this.btn.querySelector('[data-total-pages]');
      const currentPageInput = this.btn.querySelector('[data-current-page]');
      
      if (!totalPagesInput || !currentPageInput) return;
      
      const totalPages = parseInt(totalPagesInput.value, 10);
      const currentPage = parseInt(currentPageInput.value, 10);
      
      // Calculate what pages we should have preloaded
      const maxPreloadPages = 3;
      const pagesWeWant = [];
      
      for (let i = 1; i <= maxPreloadPages; i++) {
        const targetPage = currentPage + i;
        if (targetPage <= totalPages) {
          pagesWeWant.push(targetPage);
        }
      }
      
      // Check what we already have cached or in progress
      const currentUrls = Array.from(this.preloadedPages.keys()).concat(Array.from(this.preloadInProgress));
      const alreadyHandled = new Set();
      
      currentUrls.forEach(url => {
        const pageMatch = url.match(/page=(\d+)/);
        if (pageMatch) {
          alreadyHandled.add(parseInt(pageMatch[1], 10));
        }
      });
      
      // Only preload pages we don't already have
      const pagesToPreload = pagesWeWant.filter(page => !alreadyHandled.has(page));
      
      // Preload missing pages with staggered timing
      pagesToPreload.forEach((page, index) => {
        setTimeout(() => {
          this.#preloadPage(page);
        }, index * 200);
      });
    }

    /**
     * Parses response HTML and extracts items and button
     * @param {string} responseText - The HTML response text
     * @returns {Object} Object containing items and newBtn
     */
    #parseResponseData(responseText) {
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const id = 'ProductGridContainer';
      const source = html.getElementById(id).querySelector('.product-grid');
      
      const items = source ? source.querySelectorAll('.grid__item:not(.grid__item--loader):not(.card-description), content-block') : [];
      const newBtn = html.querySelector('load-more-button');
      
      return { items, newBtn };
    }

    /**
     * Handles loaded content whether from cache or fresh fetch
     * @param {NodeList} items - The product items
     * @param {HTMLElement} newBtn - The new load more button
     * @param {boolean} isPreloaded - Whether this content was preloaded
     */
    #handleLoadedContent(items, newBtn, isPreloaded) {
      const destination = document.getElementById('ProductGridContainer').querySelector('.product-grid');
      
      if (destination && items.length > 0) {
        if (isPreloaded) {
          // Instant replacement for preloaded content - NO SKELETONS!
          for (let i = 0; i < items.length; i++) {
            const item = items[i].cloneNode(true); // Clone to avoid moving original
            destination.appendChild(item);
          }
        } else {
          // Replace skeleton placeholders with real content
          this.#replaceSkeletonsWithContent(items, destination);
        }
      }
      
      // Handle button replacement
      this.#replaceLoadMoreButton(newBtn);
    }

    /**
     * Replaces the current load more button with the new one
     * @param {HTMLElement} newBtn - The new load more button element
     */
    #replaceLoadMoreButton(newBtn) {
      if (newBtn) {
        // Transfer preloaded cache to new button before cleanup
        const currentCache = new Map(this.preloadedPages);
        const currentProgress = new Set(this.preloadInProgress);
        
        // Clean up current observer
        if (this.observer) {
          this.observer.disconnect();
          this.observer = null;
        }
        
        // Replace the button
        this.parentNode.replaceChild(newBtn, this);
        
        // Transfer cache to new button if it has the same constructor
        if (newBtn.constructor === this.constructor) {
          newBtn.preloadedPages = currentCache;
          newBtn.preloadInProgress = currentProgress;
        }
      } else {
        // Clean up observer when hiding element
        this.#cleanup();
        this.classList.add('hidden');
      }
    }

    /**
     * Handles load errors
     * @param {Error} error - The error that occurred
     */
    #handleLoadError(error) {
      // Handle error silently
      
      // Remove skeleton placeholders on error
      const destination = document.getElementById('ProductGridContainer').querySelector('.product-grid');
      const skeletons = destination.querySelectorAll('.grid__item--skeleton-loadmore');
      skeletons.forEach(skeleton => skeleton.remove());
      
      // Reset button state
      const btn = this.btn;
      btn.removeAttribute('aria-disabled');
      const buttonText = btn.querySelector('.load-more-button__text');
      const spinner = btn.querySelector('.load-more-button__spinner');
      if (buttonText) buttonText.style.opacity = '1';
      if (spinner) spinner.style.display = 'none';
    }

    /**
     * Helper to get the content block pagination parameter name
     * @returns {string|null} The parameter name (e.g., "page_xxxx")
     */
    #getContentBlockParam() {
      const block = document.querySelector('content-block[data-content-block-page-param]');
      return block ? block.dataset.contentBlockPageParam : null;
    }

    /**
     * Cleans up the intersection observer and preloaded data
     */
    #cleanup() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      
      // Clear preloaded data to prevent memory leaks
      this.preloadedPages.clear();
      this.preloadInProgress.clear();
    }

    /**
     * Called when the element is removed from the DOM
     */
    disconnectedCallback() {
      this.#cleanup();
    }
  }
  customElements.define('load-more-button', LoadMoreButton);




  class LoadPrevPages extends HTMLElement {
  constructor() {
    super();
    this.initialPage = parseInt(this.dataset.initialPage) || 1;
    this.baseUrl = this.dataset.baseUrl || window.location.pathname + window.location.search;
    this.productsPerPage = parseInt(this.dataset.productsPerPage) || 24;
    this.loadedPages = new Set([this.initialPage]); // Track our own loaded pages
    
    // Listen for load-more updates to sync our page tracking
    this.setupLoadMoreCoordination();
      this.init();
    }

    init() {
      this.setupButton();
      this.syncWithScrollComponent(); // Sync initial state
      this.updateVisibility();
      this.updateButtonHref(); // Set initial href
    }

    getContentBlockParam() {
      const block = document.querySelector('content-block[data-content-block-page-param]');
      return block ? block.dataset.contentBlockPageParam : null;
    }

    /**
     * Sync our loadedPages with the main scroll component's state
     */
    syncWithScrollComponent() {
      const scrollComponent = document.querySelector('scroll-to-product-grid');
      if (scrollComponent && scrollComponent.loadedPages) {
        // Copy all loaded pages from the main component
        scrollComponent.loadedPages.forEach(page => this.loadedPages.add(page));
      }
    }

    /**
     * Setup coordination with load-more component to sync page tracking
     */
    setupLoadMoreCoordination() {
      // Listen for load-more URL updates
      document.addEventListener('loadmore:url-updated', (event) => {
        if (event.detail.source === 'load-more') {
          // Get the new page from the URL
          const newPage = this.getPageFromURL(event.detail.url);
          if (newPage && newPage > 0) {
            // Add the new page to our tracking
            this.loadedPages.add(newPage);
            // Update visibility since we might now have new pages to go back to
            this.updateVisibility();
            // Update button href to reflect new state
            this.updateButtonHref();
          }
        }
      });
    }

    /**
     * Extract page number from URL
     * @param {string} url - The URL to extract page from
     * @returns {number} Page number or 1 if no page param
     */
    getPageFromURL(url) {
      const urlObj = new URL(url, window.location.origin);
      const pageParam = urlObj.searchParams.get('page');
      return pageParam ? parseInt(pageParam, 10) : 1;
    }

    setupButton() {
      const button = this.querySelector('#load-previous-button');
      if (!button) return;

      button.addEventListener('click', (event) => {
        event.preventDefault(); // Prevent default link navigation
        event.stopPropagation(); // Stop event bubbling
        this.loadPreviousPage();
      });

      // Additional safety: prevent any form of navigation
      button.addEventListener('mousedown', (event) => {
        if (event.button === 1) { // Middle mouse button
          event.preventDefault();
        }
      });
    }

    updateVisibility() {
      const earliestLoadedPage = Math.min(...this.loadedPages);
      const shouldShow = earliestLoadedPage > 1;
      this.style.display = shouldShow ? 'block' : 'none';
    }

    /**
     * Update the button's href attribute to reflect the next previous page that would be loaded
     */
    updateButtonHref() {
      const button = this.querySelector('#load-previous-button');
      if (!button) return;

      const earliestLoadedPage = Math.min(...this.loadedPages);
      const nextPreviousPage = earliestLoadedPage - 1;

      if (nextPreviousPage < 1) {
        // No more previous pages, but this shouldn't happen since updateVisibility should hide the button
        return;
      }

      // Build the correct href for the next previous page
      let newHref;
      if (nextPreviousPage === 1) {
        // For page 1, we need the collection base URL
        // We'll construct this by removing any page parameters from the current URL
        const currentUrl = new URL(window.location);
        currentUrl.searchParams.delete('page');
        currentUrl.searchParams.delete('phcursor');
        newHref = currentUrl.pathname + (currentUrl.search || '');
      } else {
        // For other pages, build the URL with the page parameter
        const currentUrl = new URL(window.location);
        currentUrl.searchParams.set('page', nextPreviousPage.toString());
        currentUrl.searchParams.delete('phcursor');
        newHref = currentUrl.pathname + (currentUrl.search || '');
      }

      button.href = newHref;
    }

    async loadPreviousPage() {
      const button = this.querySelector('#load-previous-button');
      const spinner = button?.querySelector('.load-more-button__spinner');
      
      if (!button) return;
      
      // Debug: Check if loadedPages has been reset
      
      // Determine which page to load
      const earliestLoadedPage = Math.min(...this.loadedPages);
      const pageToLoad = earliestLoadedPage - 1;
      
      
      if (pageToLoad < 1) return; // No previous page to load
      
      // Show loading state - spinner visibility is handled by CSS when button is disabled
      button.setAttribute('aria-disabled', 'true');
      
      try {
        // Build URL for the previous page
        const pageUrl = this.buildPageURL(pageToLoad);
        
        // Fetch the page
        const response = await fetch(pageUrl);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const responseText = await response.text();
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        
        // Extract products from the response
        const sourceGrid = html.getElementById('ProductGridContainer')?.querySelector('.product-grid');
        if (!sourceGrid) throw new Error('Could not find product grid in response');
        
        const newProducts = sourceGrid.querySelectorAll('.grid__item:not(.grid__item--loader):not(.card-description)');
        
        // Get our current product grid
        const targetGrid = document.getElementById('product-grid');
        if (!targetGrid) throw new Error('Could not find target product grid');
        
        // Insert new products at the beginning
        const fragment = document.createDocumentFragment();
        Array.from(newProducts).forEach(product => {
          fragment.appendChild(product.cloneNode(true));
        });
        
        // Insert at the beginning
        targetGrid.insertBefore(fragment, targetGrid.firstChild);
        
        // Update our own tracking
        this.loadedPages.add(pageToLoad);
        
        // Update the button's href attribute to reflect the next previous page
        this.updateButtonHref();
        
        // Update URL to reflect the new "current" page (the earliest loaded page)
        this.updateURLToCurrentPage(pageToLoad);
        
        // Notify other components about the URL update (similar to load-more coordination)
        const event = new CustomEvent('loadmore:url-updated', {
          detail: { 
            url: this.buildPageURL(pageToLoad),
            source: 'load-prev'
          }
        });
        document.dispatchEvent(event);
        
        // Also update the main component if it exists
        const scrollComponent = document.querySelector('scroll-to-product-grid');
        if (scrollComponent && scrollComponent.loadedPages) {
          // Mark that a load previous operation happened (to prevent filter update interference)
          scrollComponent.lastLoadPrevTime = performance.now();
          
          // Sync the complete loadedPages state from our tracking
          this.loadedPages.forEach(page => scrollComponent.loadedPages.add(page));
          scrollComponent.currentURLPage = pageToLoad; // Update the current page tracking
          scrollComponent.initializeProductBaseline();
          scrollComponent.observeProducts();
        }
        
        // Update visibility based on new state
        this.updateVisibility();
        
      } catch (error) {
        console.error('Error loading previous page:', error);
      } finally {
        // Hide loading state - spinner visibility is handled by CSS
        button.removeAttribute('aria-disabled');
      }
    }

    buildPageURL(pageNumber) {
      const currentUrl = new URL(window.location);
      
      if (pageNumber === 1) {
        // For page 1, remove the page parameter entirely
        currentUrl.searchParams.delete('page');
      } else {
        // For other pages, set the page parameter
        currentUrl.searchParams.set('page', pageNumber.toString());
      }

      // Handle content block pagination parameter
      const cbParam = this.getContentBlockParam();
      if (cbParam) {
        if (pageNumber === 1) {
          currentUrl.searchParams.delete(cbParam);
        } else {
          currentUrl.searchParams.set(cbParam, pageNumber.toString());
        }
      }
      
      // Clean phcursor parameter for consistency
      currentUrl.searchParams.delete('phcursor');
      
      return currentUrl.pathname + (currentUrl.search || '');
    }

    /**
     * Update the browser URL and canonical URL to reflect the current page
     * @param {number} pageNumber - The page number to update to
     */
    updateURLToCurrentPage(pageNumber) {
      let newUrl = this.buildPageURL(pageNumber);
      
      // Clean content block param for display
      const cbParam = this.getContentBlockParam();
      if (cbParam) {
        try {
          const urlObj = new URL(newUrl, window.location.origin);
          urlObj.searchParams.delete(cbParam);
          newUrl = urlObj.pathname + urlObj.search;
        } catch (e) {
          // Ignore
        }
      }
      
      // Update browser URL without triggering page reload
      window.history.replaceState({}, '', newUrl);
      
      // Update canonical URL
      this.updateCanonicalURL(newUrl);
    }

    /**
     * Update canonical URL in document head
     * @param {string} url - The new canonical URL path
     */
    updateCanonicalURL(url) {
      const canonicalLink = document.querySelector('link[rel="canonical"]');
      if (canonicalLink) {
        // Create full URL from relative path
        const fullUrl = new URL(url, window.location.origin).href;
        canonicalLink.href = fullUrl;
      }
    }

  }
  customElements.define('load-prev-pages', LoadPrevPages);